import {useMemo} from 'react';

import {useSelector} from 'react-redux';

import useChat from '~/shared/hooks/useChat';
import useOpenSignIn from '~/shared/hooks/useOpenSignIn';
import {logout} from '~/shared/services/auth';
import {selectUserData, selectUserDevice} from '~/shared/store/selectors';
import {getLocalizationService} from '~/shared/services/localisationService';
import {BECOME_A_COURIER_URL} from '~/shared/consts/commonConsts';
import ActionMenu from '~/shared/components/ActionMenu';
import {isJustEat} from '~/shared/config';
import usePersonalAreaSections from '~/common/hooks/usePersonalAreaSections';

const PageHeaderActionMenu = ({shouldShowExit = true, burgerMenu = false, CustomMenuComponent = null, className = ''}) => {
  const userData = useSelector(selectUserData);
  const userDevice = useSelector(selectUserDevice);
  const actionMenuLinks = useActionMenuLinks(userData, shouldShowExit);

  return <ActionMenu actionMenuLinks={actionMenuLinks} burgerMenu={burgerMenu} CustomMenuComponent={CustomMenuComponent} className={className} userData={userData} userDevice={userDevice}/>;
};

export default PageHeaderActionMenu;

function useActionMenuLinks(userData, shouldShowExit = true) {
  const {currentLanguageKey} = getLocalizationService();
  const sections = usePersonalAreaSections();
  const {openLogin} = useOpenSignIn();

  const {userEnabledFeatures, isCompanyUser} = userData;
  const hasLoyaltyProgramFeatureFlag = userEnabledFeatures?.includes('LoyaltyProgram');

  const {openChat} = useChat();
  return useMemo(
    () => [
      {
        textKey: 'log_in',
        onClick: () => openLogin(),
        isHighlighted: false,
        openInNewWindow: false,
        compAdminOnly: false,
        loggedInUserOnly: false,
        loggedOutUserOnly: true,
        isAvailable: true,
        isVisibleOnMobile: true,
        toggleMenu: true,
      },
      {
        textKey: 'eat_outside',
        url: 'Restaurants/Search/Sitting',
        isHighlighted: true,
        openInNewWindow: false,
        companyUserOnly: true,
        loggedInUserOnly: true,
        isAvailable: true,
        isVisibleOnMobile: false,
        toggleMenu: false,
        useLegacy: true,
      },
      {
        textKey: 'reorder',
        url: '/reorder',
        isHighlighted: false,
        openInNewWindow: false,
        loggedInUserOnly: true,
        isAvailable: true,
        isVisibleOnMobile: true,
        showOnMobileOnly: true,
        toggleMenu: true,
        useLegacy: false,
      },
      ...Object.values(sections || {}).map(section => ({
        textKey: (isCompanyUser && section.b2bTitle) ? section.b2bTitle : section.actionMenuTitle || section.title,
        url: `/personal-area${section.query ? `?section=${section.query}` : ''}`,
        isHighlighted: false,
        onClick: section.onClick,
        openInNewWindow: false,
        loggedInUserOnly: true,
        isAvailable: true,
        isVisibleOnMobile: Boolean(section.query),
        showOnMobileOnly: (isCompanyUser && section.showForCompanyUserOnDesktop) ? false : Boolean(section.query),
        toggleMenu: true,
      })),
      {
        textKey: 'loyalty_program',
        url: 'Account/ServiceArea',
        isHighlighted: false,
        openInNewWindow: false,
        loggedInUserOnly: true,
        isAvailable: hasLoyaltyProgramFeatureFlag,
        isVisibleOnMobile: true,
        companyUserOnly: true,
        toggleMenu: false,
        useLegacy: true,
      },
      {
        textKey: 'become_a_courier',
        url: BECOME_A_COURIER_URL[currentLanguageKey],
        openInNewWindow: true,
        isVisibleOnMobile: true,
        isAvailable: true,
        ...(isJustEat && {rel: 'nofollow'}),
      },
      {
        textKey: 'live_chat_with_service_representative',
        onClick: () => openChat(),
        isHighlighted: false,
        openInNewWindow: false,
        compAdminOnly: false,
        loggedInUserOnly: false,
        isAvailable: true,
        isVisibleOnMobile: true,
        toggleMenu: true,
      },
      {
        textKey: 'enter_company_representative_interface',
        url: 'CompAdmin/',
        isHighlighted: false,
        openInNewWindow: false,
        compAdminOnly: true,
        loggedInUserOnly: true,
        isAvailable: true,
        isVisibleOnMobile: true,
        toggleMenu: false,
        useLegacy: true,
      },
      {
        textKey: 'the_order_has_arrived_send_message',
        url: 'CompAdmin/Reports/ThinDailyByUserReport',
        isHighlighted: false,
        openInNewWindow: true,
        compAdminOnly: true,
        loggedInUserOnly: true,
        isAvailable: true,
        isVisibleOnMobile: true,
        toggleMenu: false,
        useLegacy: true,
      },
      {
        ...(shouldShowExit && {
          textKey: 'exit',
          onClick: () => logout(),
          isHighlighted: false,
          openInNewWindow: false,
          compAdminOnly: false,
          loggedInUserOnly: false,
          isAvailable: true,
          isVisibleOnMobile: true,
          toggleMenu: false,
        }),
      },
    ],
    [hasLoyaltyProgramFeatureFlag, currentLanguageKey, shouldShowExit, openLogin, isCompanyUser, openChat, sections],
  );
}
